import { CommunicationsApiSdk } from '../../api/CommunicationsApiSdk';
import { BrowserStorage } from '@estee/elc-universal-utils';
import { EmailOptIn, IEmailOptIn } from '../domain/entities/EmailOptIn';
import { observable, action, computed } from 'mobx';
import {
    COMMUNICATION_TYPE,
    SUBSCRIBE_SOURCE,
    UNSUBSCRIBE_SOURCE,
    COOKIE_USER_EMAIL
} from '../constants/EmailOptIn';

import {
    SIGN_UP_SOURCE,
    ACCOUNT_SOURCE,
    CHECKOUT_GUEST_SOURCE,
    APPOINTMENT_BOOKED_GUEST_SOURCE
} from '../constants/SubscribeSource';
import { emit, events } from '@estee/elc-service-bus';

export interface IEmailOptInPayload {
    source: string;
    email: string;
}

export type IPayload = IEmailOptInPayload;

export interface IEmaiOptInRepositoryConfig {
    apiSdk: CommunicationsApiSdk;
}

export interface IEmailOptInSources {
    [key: string]: string;
}

const EMAIL_OPT_IN_SOURCES: IEmailOptInSources = {
    [SIGN_UP_SOURCE]: 'registration',
    [ACCOUNT_SOURCE]: 'account',
    [CHECKOUT_GUEST_SOURCE]: 'checkout_guest',
    [APPOINTMENT_BOOKED_GUEST_SOURCE]: 'appointment_booked_guest'
};

export class EmailOptInRepository {
    @observable private api: CommunicationsApiSdk;
    @observable private internalEmailOptIn: EmailOptIn = new EmailOptIn();
    @observable private emailOptInResp: EmailOptIn | null = null;

    constructor(config: IEmaiOptInRepositoryConfig) {
        this.api = config.apiSdk;
    }

    public emailOptInRequest = async (payload: IEmailOptInPayload) => {
        if (this.internalEmailOptIn.emailSubscribed) {
            const response = await this.api.emailOptInRequest({
                emailAddress: payload.email,
                subscribeSource: EMAIL_OPT_IN_SOURCES[payload.source],
                communicationType: [COMMUNICATION_TYPE.EMAIL]
            });
            emit(events.SUBSCRIBED_FOR_EMAILS);
            this.setEmailOptInResponse(response[0]);
        }
    };

    public getEmailOptInRequest = async (email: string): Promise<IEmailOptIn[]> => {
        const emailOptInResponse = await this.api.getEmailOptInRequest(email);
        this.setEmailOptInResponse(emailOptInResponse[0]);
        this.setInternalEmailOptInResponse(emailOptInResponse[0]);

        return emailOptInResponse;
    };

    @action
    public setEmailOptInResponse = (emailOptInResponse: IEmailOptIn) => {
        this.emailOptInResp = EmailOptIn.FROM_JSON(emailOptInResponse);
    };

    @action
    public setInternalEmailOptInResponse = (emailOptInResponse: IEmailOptIn) => {
        this.internalEmailOptIn = EmailOptIn.FROM_JSON(emailOptInResponse);
    };

    @action
    public setInternalEmailOptIn = (emailOptIn: EmailOptIn) => {
        if (!this.internalEmailOptIn) {
            this.internalEmailOptIn = emailOptIn;
        }

        // update view model
        if (this.emailOptInResp) {
            this.internalEmailOptIn.updateEmailSubscribed(this.emailOptInResp.emailSubscribed);
        }
    };

    public updateEmailOptInRequest = async (payload: IPayload) => {
        if (this.emailOptInResponse && this.emailOptInResponse.id && this.emailOptInResp) {
            if (this.shouldUpdateSubscription) {
                const subscribeSourceKey = this.emailOptIn.emailSubscribed
                    ? SUBSCRIBE_SOURCE
                    : UNSUBSCRIBE_SOURCE;

                await this.api.updateEmailOptInRequest(this.emailOptInResponse.id, {
                    fields: {
                        emailSubscribed: `${this.internalEmailOptIn.emailSubscribed}`,
                        [subscribeSourceKey]: EMAIL_OPT_IN_SOURCES[ACCOUNT_SOURCE]
                    }
                });
                if (subscribeSourceKey === SUBSCRIBE_SOURCE) {
                    emit(events.SUBSCRIBED_FOR_EMAILS);
                }
                this.emailOptInResp.updateEmailSubscribed(this.emailOptIn.emailSubscribed);
            }

            return;
        }
        await this.emailOptInRequest({
            email: payload.email,
            source: ACCOUNT_SOURCE
        });
    };

    @computed
    public get emailOptIn() {
        return this.internalEmailOptIn;
    }

    @computed
    public get emailOptInResponse() {
        return this.emailOptInResp;
    }

    private getCookieUserEmail = () => {
        return BrowserStorage.getCookie(COOKIE_USER_EMAIL);
    };

    public getUserEmail = (payload: IEmailOptInPayload) => {
        return payload.email || this.getCookieUserEmail() || '';
    };

    @computed
    public get shouldUpdateSubscription() {
        return (
            this.emailOptInResp &&
            this.emailOptIn.emailSubscribed !== this.emailOptInResp.isEmailSubscribed
        );
    }

    public clearEmailOptIn = () => {
        if (this.internalEmailOptIn) {
            this.internalEmailOptIn.clearEmailSubscribed();
        }
        if (this.emailOptInResponse) {
            this.emailOptInResponse.clearEmailSubscribed();
        }
    };
}
